import styled from "styled-components";
import { formatPrice } from "../../utils/format";

// A faithful first-page cover scorecard, rebuilt in React from report-document
// content — the "quality anchor" / proof-of-work. Shared by the Personalize
// editor and the Share step. Everything degrades gracefully if a field is absent.

const CoverCard = styled.section`
  border: 1px solid #e5e7eb;
  border-top: 4px solid ${(p) => p.$accent || "#0e7e7a"};
  border-radius: 16px;
  padding: 22px 24px;
  margin-bottom: 22px;
  background: #ffffff;
  box-shadow: 0 4px 18px rgba(11, 28, 30, 0.06);
`;

const CoverTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const CoverLogo = styled.img`
  height: 26px;
  max-width: 160px;
  object-fit: contain;
`;

const CoverKicker = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${(p) => p.$accent || "#0e7e7a"};
`;

const CoverPreparedBy = styled.div`
  font-size: 0.82rem;
  color: #6b7280;
  text-align: right;
  line-height: 1.4;
`;

const CoverAddress = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(p) => p.$accent || "#0b1c1e"};
  margin: 0 0 4px;
  line-height: 1.2;
`;

const CoverTagline = styled.p`
  font-size: 0.98rem;
  font-style: italic;
  color: #4a4a4a;
  margin: 0 0 14px;
`;

const CoverFacts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  border-top: 1px solid #eef1f1;
  border-bottom: 1px solid #eef1f1;
  margin: 12px 0 16px;
`;

const CoverFact = styled.div`
  flex: 1;
  min-width: 64px;
  text-align: center;
  padding: 10px 6px;
  border-right: 1px solid #eef1f1;

  &:last-child {
    border-right: none;
  }
`;

const CoverFactValue = styled.div`
  font-size: 1.15rem;
  font-weight: 700;
  color: #0b1c1e;
`;

const CoverFactLabel = styled.div`
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin-top: 2px;
`;

const CoverPricing = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: #f7fafb;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const CoverPricingLabel = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
`;

const CoverRange = styled.div`
  font-size: 1.35rem;
  font-weight: 700;
  color: #0b1c1e;
`;

const CoverVerdict = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 999px;
  background: #e6f4f3;
  color: #0b605d;
  white-space: nowrap;
`;

// Word ratings (Strong / Good / At Market…), mirroring the PDF cover's quick
// stats — Listino doesn't grade, so no numeric scores or bars.
const CoverRatings = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const RatingPill = styled.div`
  flex: 1 1 30%;
  min-width: 130px;
  background: #f8fafa;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 12px;
`;

const RatingLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin-bottom: 3px;
`;

const RatingValue = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.92rem;
  font-weight: 700;
  color: #0b1c1e;
`;

/** @type {import("styled-components").StyledComponent<"span", any, { $color?: string }, never>} */
const RatingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(/** @type {{ $color?: string }} */ p) => p.$color};
`;

const STAT_DOT_COLORS = { green: "#22c55e", yellow: "#f59e0b", red: "#ef4444" };
/** @typedef {{ label: string, value: string, color?: string }} CoverStat */

/**
 * @param {string | undefined} color
 * @param {string} fallback
 */
const ratingDotColor = (color, fallback) =>
  color && Object.prototype.hasOwnProperty.call(STAT_DOT_COLORS, color)
    ? STAT_DOT_COLORS[/** @type {keyof typeof STAT_DOT_COLORS} */ (color)]
    : fallback;

const CoverOpps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CoverOppItem = styled.div`
  font-size: 0.92rem;
  color: #374151;
  line-height: 1.4;

  b {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${(p) => p.$accent || "#0e7e7a"};
    color: #ffffff;
    font-size: 0.72rem;
    margin-right: 8px;
    vertical-align: middle;
  }
`;

const ViewDocLink = styled.a`
  display: inline-block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #0e7e7a;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ViewDocRow = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
`;

const ViewDocNote = styled.span`
  font-size: 0.8rem;
  color: #6b7280;
`;

/** @param {unknown} n */
const fmtMoney = (n) => {
  const v = Number(n);
  if (!v || Number.isNaN(v)) return "";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  return `$${Math.round(v / 1000)}K`;
};

/** @param {unknown} n */

/** @param {string | null | undefined} iso */
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

/**
 * @param {{
 *   doc: Record<string, any>,
 *   sections?: any[],
 *   branding?: Record<string, any>,
 *   showViewDoc?: boolean,
 * }} props
 */
const CoverPreview = ({ doc, sections, branding = {}, showViewDoc = true }) => {
  const content = doc.content || {};
  const pd = content.property_details || {};
  const cover = content.cover_page_data || {};
  const secs = /** @type {Record<string, any>[]}  */ (sections || content.sections || []);
  const comp = secs.find((s) => s.__type__ === "ComparableAnalysisSection");
  // The PDF cover's word ratings (label/value/color) — first three, no scores.
  const quickStats = /** @type {CoverStat[]} */ (
    Array.isArray(cover.quick_stats) ? cover.quick_stats : []
  );
  const ratings = quickStats.filter((stat) => stat?.label && stat?.value).slice(0, 3);
  const opps = /** @type {string[]} */ ((cover.top_opportunities || []).slice(0, 3));
  const accent = branding.accent_color || "#0e7e7a";
  // PropertyReport.to_dict() serializes datetimes as { __datetime__: iso }.
  const preparedDate = fmtDate(content.generated_at?.__datetime__ || content.generated_at);
  const hasFacts =
    pd.bedrooms || pd.bathrooms || pd.square_feet || pd.year_built || pd.listing_price;
  // API bed/bath counts arrive as strings like "4.0" — match the PDF cover,
  // which prints "4" (and keeps real halves like "2.5").
  const cleanCount = (/** @type {unknown} */ v) => {
    const n = Number(v);
    return Number.isFinite(n) ? String(n % 1 === 0 ? Math.trunc(n) : n) : String(v);
  };
  const preparedBy =
    (branding.agent_name || "") + (branding.brokerage ? `, ${branding.brokerage}` : "");
  return (
    <CoverCard $accent={accent}>
      <CoverTop>
        {branding.logo_url ? (
          <CoverLogo src={branding.logo_url} alt="" />
        ) : (
          <CoverKicker $accent={accent}>Overview</CoverKicker>
        )}
        {(preparedBy || preparedDate) && (
          <CoverPreparedBy>
            {preparedBy && (
              <>
                Prepared by {preparedBy}
                {preparedDate && <br />}
              </>
            )}
            {preparedDate && `${preparedBy ? "" : "Prepared "}${preparedDate}`}
          </CoverPreparedBy>
        )}
      </CoverTop>
      <CoverAddress $accent={accent}>{doc.address || "Your listing"}</CoverAddress>
      {cover.tagline && <CoverTagline>{cover.tagline}</CoverTagline>}
      {hasFacts && (
        <CoverFacts>
          {pd.bedrooms ? (
            <CoverFact>
              <CoverFactValue>{cleanCount(pd.bedrooms)}</CoverFactValue>
              <CoverFactLabel>Beds</CoverFactLabel>
            </CoverFact>
          ) : null}
          {pd.bathrooms ? (
            <CoverFact>
              <CoverFactValue>{cleanCount(pd.bathrooms)}</CoverFactValue>
              <CoverFactLabel>Baths</CoverFactLabel>
            </CoverFact>
          ) : null}
          {pd.square_feet ? (
            <CoverFact>
              <CoverFactValue>{Number(pd.square_feet).toLocaleString()}</CoverFactValue>
              <CoverFactLabel>Sq Ft</CoverFactLabel>
            </CoverFact>
          ) : null}
          {pd.year_built ? (
            <CoverFact>
              <CoverFactValue>{pd.year_built}</CoverFactValue>
              <CoverFactLabel>Built</CoverFactLabel>
            </CoverFact>
          ) : null}
          {pd.listing_price ? (
            <CoverFact>
              <CoverFactValue>{fmtMoney(pd.listing_price)}</CoverFactValue>
              <CoverFactLabel>List price</CoverFactLabel>
            </CoverFact>
          ) : null}
        </CoverFacts>
      )}
      {comp?.suggested_range_low && comp?.suggested_range_high && (
        <CoverPricing>
          <div>
            <CoverPricingLabel>Suggested list range</CoverPricingLabel>
            <CoverRange>
              {formatPrice(comp.suggested_range_low)} – {formatPrice(comp.suggested_range_high)}
            </CoverRange>
          </div>
          {comp.pricing_verdict && <CoverVerdict>{comp.pricing_verdict}</CoverVerdict>}
        </CoverPricing>
      )}
      {ratings.length > 0 && (
        <CoverRatings>
          {ratings.map((stat) => (
            <RatingPill key={stat.label}>
              <RatingLabel>{stat.label}</RatingLabel>
              <RatingValue>
                <RatingDot $color={ratingDotColor(stat.color, accent)} />
                {stat.value}
              </RatingValue>
            </RatingPill>
          ))}
        </CoverRatings>
      )}
      {opps.length > 0 && (
        <CoverOpps>
          {opps.map((opp, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: opportunities are a fixed ordered list of prose strings
            <CoverOppItem key={i} $accent={accent}>
              <b>{i + 1}</b>
              {opp}
            </CoverOppItem>
          ))}
        </CoverOpps>
      )}
      {showViewDoc && doc.report_pdf_url && (doc.rendered_version || 0) >= 1 && (
        // The full document is the proof-of-work — keep it one tap away even
        // while edits are pending a re-render; the note keeps the agent from
        // reading a stale PDF as a lost save.
        <ViewDocRow>
          <ViewDocLink href={doc.report_pdf_url} target="_blank" rel="noopener noreferrer">
            View full document →
          </ViewDocLink>
          {(doc.rendered_version || 0) < (doc.version || 0) && (
            <ViewDocNote>Your newest edits appear after you Share.</ViewDocNote>
          )}
        </ViewDocRow>
      )}
    </CoverCard>
  );
};

export default CoverPreview;
